import { Typography } from '@forethought-technologies/forethought-elements';
import { Box, Fade, IconButton, useTheme } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { recordKnowledgeClick } from 'actions/workflows-v2/workflowsV2';
import type { KnowledgeResult, KnowledgeSearchResponse } from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectFreeFormQuery,
  selectFloatingBarSearchResult,
  setFloatingBarSearchResult,
} from 'reducers/workflows';
import { htmlDecode } from 'utils/htmlDecode';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';
import { useWidgetArticleSearch } from './App/hooks/useWidgetArticleSearch';
import { selectWindowDimensions } from 'reducers/main';
import { selectPlacement } from 'reducers/config';

const MAX_HEIGHT = 300;

export const FloatingArticleSuggestions = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const freeFormQuery = useAppSelector(selectFreeFormQuery);
  const floatingBarSearchResult = useAppSelector(selectFloatingBarSearchResult);
  const windowDimensions = useAppSelector(selectWindowDimensions);
  const placement = useAppSelector(selectPlacement);

  /**
   * We need to calculate how much space we have to show the results
   * based on the placement of the floating bar and then we need to resize
   * the iframe to fit the results.
   *
   * If the floating bar is placed at the top or the bottom, we are using
   * the default max height because that would mean the screen is roughly 500px
   * tall and we can show the results without any issues.
   */
  const maxHeight = (() => {
    if (placement === 'middle' && windowDimensions.iframeRect?.top) {
      /**
       * Distance from the bottom of the viewport to the top of the iframe
       *
       * 125 takes into account the size of the floating bar input and
       * the spacing of the iframe around it
       */
      const calculatedMaxHeight =
        windowDimensions.innerHeight - windowDimensions.iframeRect.top - 125;

      if (calculatedMaxHeight < 0) {
        // if the calculated max height is negative, we need to set it to 0
        return 0;
      }

      return Math.min(MAX_HEIGHT, calculatedMaxHeight);
    }

    return MAX_HEIGHT;
  })();

  // used to trigger the search when the freeFormQuery changes
  useWidgetArticleSearch({
    origin: 'floating_bar',
    query: freeFormQuery,
  });

  const onResultClick = ({
    document,
    fullResult,
  }: {
    document: KnowledgeResult;
    fullResult: KnowledgeSearchResponse;
  }) => {
    dispatch(recordKnowledgeClick({ document, fullResult }));
  };

  React.useEffect(() => {
    setTimeout(() => {
      postIframeResizeEvent();
    });
  }, [floatingBarSearchResult, maxHeight]);

  if (!floatingBarSearchResult || !floatingBarSearchResult.results.length) {
    return null;
  }

  return (
    <Fade in={Boolean(floatingBarSearchResult.results.length)}>
      <Box
        className='forethought-scrollbar'
        sx={{
          backgroundColor: theme.palette.colors.white,
          border: `1px solid ${theme.palette.colors.grey[300]}`,
          borderRadius: '4px',
          boxShadow:
            '2px 5px 8px 0px rgba(3, 17, 38, 0.1071), 0px 0px 1px 0px rgba(0, 12, 32, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginY: '8px',
          maxHeight: `${maxHeight}px`,
          overflowX: 'hidden',
          overflowY: 'auto',
          padding: 2,
        }}
      >
        {/* label */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            color={theme.palette.colors.grey[600]}
            component='div'
            variant='font12'
          >
            Top article results
          </Typography>
          <IconButton
            aria-label='Clear search results'
            onClick={() => {
              dispatch(setFloatingBarSearchResult(undefined));
            }}
          >
            <IconX color={theme.palette.colors.grey[600]} size={12} />
          </IconButton>
        </Box>
        {/* results */}
        {floatingBarSearchResult.results.map((result, index) => (
          <Box
            component='a'
            href={result.link}
            key={index}
            onClick={() => {
              onResultClick({
                document: result,
                fullResult: floatingBarSearchResult,
              });
            }}
            rel='noopener noreferrer'
            sx={{
              color: 'inherit',
              cursor: 'pointer',
              gap: '4px',
              textDecoration: 'none',
            }}
            target='_blank'
          >
            <Typography component='div' variant='font14'>
              {result.title}
            </Typography>
            <Box
              sx={{
                color: theme.palette.colors.grey[500],
                fontSize: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                color={theme.palette.colors.grey[500]}
                variant='font12'
              >
                {htmlDecode(result.preview)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Fade>
  );
};
